import React from "react";
import { Section } from "./Section";
import { FileSignature, BadgeCheck, Globe2 } from "lucide-react";

const OFFERINGS = [
  {
    id: "hire-contract",
    icon: FileSignature,
    title: "Contract",
    description:
      "Vetted engineers, sysadmins and SREs on flexible 3–12 month engagements. Ramp in weeks, not quarters.",
    points: ["3–12 month engagements", "Vetted senior talent", "Replace within 14 days"],
  },
  {
    id: "hire-permanent",
    icon: BadgeCheck,
    title: "Permanent",
    description:
      "Direct-hire placement for cloud, DevOps, security and platform roles — backed by a 90-day guarantee.",
    points: ["Direct hire / FTE", "90-day replacement", "Niche & senior roles"],
  },
  {
    id: "hire-global",
    icon: Globe2,
    title: "Global Delivery",
    description:
      "Distributed pods working in US time zones. Same-day overlap, predictable velocity, no late-night handoffs.",
    points: ["US-time aligned", "Pod-based delivery", "Single point of contact"],
  },
];

export const HiringSolutions = () => {
  return (
    <Section id="hiring" className="bg-zinc-950 py-24 lg:py-32 border-t border-white/5">
      <div className="reveal-up max-w-2xl">
        <div className="text-xs uppercase tracking-[0.22em] text-[#FF4500] font-semibold">
          Hiring solutions
        </div>
        <h2
          className="mt-4 text-4xl sm:text-5xl font-black tracking-[-0.02em] text-white"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Talent that ships,
          <br />
          <span className="text-zinc-500">on the timeline you actually need.</span>
        </h2>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
        {OFFERINGS.map((o) => {
          const Icon = o.icon;
          return (
            <div
              key={o.id}
              data-testid={o.id}
              className="reveal-up bg-zinc-950 p-8 lg:p-10 flex flex-col hover:bg-zinc-900 transition-colors duration-200 group"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-white/5 text-white group-hover:bg-[#FF4500] transition-colors duration-200">
                <Icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <h3
                className="mt-6 text-xl font-bold tracking-tight text-white"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {o.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                {o.description}
              </p>
              <ul className="mt-6 space-y-2 border-t border-white/10 pt-5">
                {o.points.map((p) => (
                  <li
                    key={`${o.id}-${p}`}
                    className="flex items-start gap-2 text-xs text-zinc-300"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-1 w-1 h-1 rounded-full bg-[#FF4500] flex-shrink-0"
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="reveal-up mt-14 flex flex-col lg:flex-row flex-wrap items-start lg:items-center gap-4 lg:gap-6 border border-white/10 bg-white/5 rounded-2xl px-6 py-5 lg:px-8 xl:px-10">
        <div className="text-sm text-zinc-300">
          <span className="text-white font-semibold">Global Delivery, US-Time Aligned.</span>{" "}
          Distributed teams with 4–6 hours of daily overlap with US business hours.
        </div>
        <a
          href="#contact"
          data-testid="hire-contact-cta"
          className="ml-auto inline-flex items-center gap-2 bg-[#FF4500] hover:bg-[#ff5a1f] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
        >
          Talk to talent
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </Section>
  );
};

export default HiringSolutions;
